import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';

/**
 * Section that displays 3 related services as clickable cards.
 * Adds strong contextual internal links between SEO landing pages — Google reads
 * these as topical clusters which improves crawl priority and rankings.
 *
 * Usage:
 *   <RelatedServices
 *     services={[
 *       { title: '...', description: '...', path: '/...', accent: 'teal' },
 *       ...
 *     ]}
 *   />
 */
const RelatedServices = ({ services = [] }) => {
  if (!services.length) return null;

  const accentMap = {
    teal: 'border-teal-200 hover:border-teal-500 group-hover:text-teal-600',
    blue: 'border-blue-200 hover:border-blue-500 group-hover:text-blue-600',
    green: 'border-green-200 hover:border-green-500 group-hover:text-green-600',
    indigo: 'border-indigo-200 hover:border-indigo-500 group-hover:text-indigo-600',
    purple: 'border-purple-200 hover:border-purple-500 group-hover:text-purple-600',
    emerald: 'border-emerald-200 hover:border-emerald-500 group-hover:text-emerald-600',
    amber: 'border-amber-200 hover:border-amber-500 group-hover:text-amber-600',
    rose: 'border-rose-200 hover:border-rose-500 group-hover:text-rose-600',
    orange: 'border-orange-200 hover:border-orange-500 group-hover:text-orange-600'
  };

  return (
    <section className="py-20 px-4 bg-white border-t border-gray-100">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">
            Related Services
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
            Explore More Solutions
          </h2>
          <p className="text-lg text-gray-600 mt-3 max-w-2xl mx-auto">
            Round out your compliance and growth strategy with these complementary services.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((svc, idx) => (
            <Link
              key={idx}
              to={svc.path}
              data-testid={`related-service-${idx}`}
              className="block group"
            >
              <Card
                className={`border-2 ${accentMap[svc.accent] || accentMap.teal} transition-all hover:shadow-lg h-full`}
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <h3 className={`text-lg font-bold text-gray-900 mb-2 transition-colors ${accentMap[svc.accent] || ''}`}>
                    {svc.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 flex-1">{svc.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-gray-700 group-hover:gap-2 transition-all">
                    Learn more
                    <ArrowRight size={16} />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedServices;
